import { initializeApp, type FirebaseOptions } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider, type AppCheck } from 'firebase/app-check';
import { browserSessionPersistence, connectAuthEmulator, initializeAuth } from 'firebase/auth';
import { connectFirestoreEmulator, initializeFirestore, memoryLocalCache } from 'firebase/firestore';
import { measurementId, useEmulators } from './config';

const env = import.meta.env;

function required(name: keyof ImportMetaEnv): string {
  const value = env[name];
  if (!value) {
    throw new Error(`Missing ${name}. Copy web/.env.example to web/.env.local and fill it in.`);
  }
  return value;
}

const options: FirebaseOptions = useEmulators
  ? { apiKey: 'demo-api-key', authDomain: 'localhost', projectId: 'demo-carshop', appId: 'demo-app' }
  : {
      apiKey: required('VITE_FIREBASE_API_KEY'),
      authDomain: required('VITE_FIREBASE_AUTH_DOMAIN'),
      projectId: required('VITE_FIREBASE_PROJECT_ID'),
      appId: required('VITE_FIREBASE_APP_ID'),
      // Optional: the SDK fetches it itself, this is the fallback (see analytics.ts).
      ...(measurementId ? { measurementId } : {}),
    };

export const app = initializeApp(options);

// App Check is enforced on Firestore in the cloud; the emulators don't check it.
// The staff API (lib/staff.ts) sends the same token.
export let appCheck: AppCheck | null = null;
if (!useEmulators) {
  if (env.VITE_APPCHECK_DEBUG_TOKEN) {
    (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN: string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
      env.VITE_APPCHECK_DEBUG_TOKEN;
  }
  appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(required('VITE_RECAPTCHA_SITE_KEY')),
    isTokenAutoRefreshEnabled: true,
  });
}

// Session persistence: closing the tab on a shared workshop PC signs the user out.
export const auth = initializeAuth(app, { persistence: browserSessionPersistence });

// Memory cache only: no client data is left in the browser's IndexedDB.
export const db = initializeFirestore(app, { localCache: memoryLocalCache() });

if (useEmulators) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
}
